import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader, useAlert } from '@frontend/design-system';
import { useSearchPatient } from '../../../hooks/api-hooks';
import { useConfigShallowStore } from '../../../stores';
import { useKioskFillingExperienceContext } from '../provider';
import { PatronForm, PatronFormValues } from './patron-form';
import { PatronFormWelcomeScreen } from './patron-form-welcome-screen';

export const ExistingPatronForm = ({ onWelcomeAccept }: { onWelcomeAccept?: () => void }) => {
  const { t } = useTranslation('digital-forms-experience');
  const alerts = useAlert();
  const navigate = useNavigate();
  const [requireExistingPatronOptionalFields, setRequireExistingPatronOptionalFields] = useState(false);
  const { locationId, documentType, patronType, documentId } = useKioskFillingExperienceContext();
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(true);
  const { searchPatient, isSearching } = useSearchPatient();
  const { setPerson } = useConfigShallowStore('setPerson');

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = useCallback(
    (values: PatronFormValues) => {
      searchPatient({
        firstName: values.firstName,
        lastName: values.lastName,
        birthdate: values.birthdate,
        phoneNumber: values.mobilePhone,
        companyId: locationId,
      }).then((response) => {
        // Unmounted means the session was abandoned (e.g. idle redirect); don't restore the expired identity
        if (!isMountedRef.current) return;
        if (response.isSyncAppError) {
          alerts.error(
            t(
              'Our office is experiencing some technical difficulties right now. Please contact our office to proceed further.'
            )
          );
          return;
        }
        if (response.hasMultipleResults) {
          if (requireExistingPatronOptionalFields) {
            alerts.error(
              t(
                'There is more than one patient registered with this information. Please contact our office to proceed further.'
              )
            );
          } else {
            setRequireExistingPatronOptionalFields(true);
            alerts.info(t('Enter the following information to continue'));
          }
          return;
        }

        if (!response.success) {
          alerts.error(
            t(
              'There is no such patient registered with us. Enter the correct information or register as a new patient.'
            )
          );
          return;
        }

        const { patientDetails } = response;

        setPerson({
          ...patientDetails,
        });
        if ((documentType === 'form' || documentType === 'packet') && documentId) {
          navigate({
            to: '/form',
            search: {
              [documentType === 'packet' ? 'packetId' : 'formId']: documentId,
              companyId: locationId,
              origin: 'kiosk',
            },
          });
        } else {
          navigate({ to: `/kiosk/${locationId}` });
        }
      });
    },
    [
      alerts,
      documentId,
      documentType,
      locationId,
      navigate,
      requireExistingPatronOptionalFields,
      searchPatient,
      setPerson,
      t,
    ]
  );

  if (showWelcomeScreen) {
    return (
      <PatronFormWelcomeScreen
        locationId={locationId}
        onAccept={() => {
          setShowWelcomeScreen(false);
          onWelcomeAccept?.();
        }}
      />
    );
  }

  return (
    <>
      <ContentLoader show={isSearching} />
      <PatronForm
        onSubmit={handleSubmit}
        patronType={patronType}
        requireExistingPatronOptionalFields={requireExistingPatronOptionalFields}
      />
    </>
  );
};
